import { BaseService } from '@common/http-layer/service/base.service'
import { readAmbientNuxtApp } from '@common/core-layer/utils/ambient-nuxt-app'
import { useLocationStore } from '@common/location-layer/store/location.store'
import type { Pinia } from 'pinia'

export class ProductLearnerService extends BaseService {
  constructor() {
    super({
      baseURL: 'REST_API_URLS',
      basePath: '',
      headers: {
        version: '2021-07-28',
      },
    })
  }

  /**
   * Only reached when a caller omits `locationId` (every current caller passes it
   * explicitly). Reads the location store directly instead of `useLocation()` and
   * guards it behind an ambient-Pinia check — on SSR, outside an active Nuxt/Pinia
   * context (e.g. the courses-access-guard middleware's async query fetchFn),
   * `useLocationStore()` throws reading `pinia._s`. Mirrors the same guard
   * `BaseService.prepareRequest` already applies before reading the auth store.
   */
  private resolveFallbackLocationId(): string | undefined {
    const ambientPinia = readAmbientNuxtApp()?.$pinia as Pinia | undefined
    if (!ambientPinia) {
      this.logger.warn('Not in Nuxt context, skipping location store read', {
        labels: { context: 'ProductLearnerService.endpoint' },
      })
      return undefined
    }
    return useLocationStore(ambientPinia).locationId || undefined
  }

  private endpoint(path: string = '', locationId?: string): string {
    const resolvedLocationId = locationId || this.resolveFallbackLocationId()
    if (!resolvedLocationId) throw new Error('Location ID is required')
    return `/courses/learners/locations/${resolvedLocationId}/courses${path}`
  }

  async getProductById(params: { courseId: string; locationId: string }) {
    const { data } = await this.get(this.endpoint(`/${params.courseId}`, params.locationId), {
      productId: params.courseId,
      locationId: params.locationId,
    })
    return data
  }

  async checkProductAccess(params: { courseId: string; source: string; locationId: string }) {
    const { data } = await this.get(this.endpoint(`/${params.courseId}/access?source=${params.source}`, params.locationId), {
      productId: params.courseId,
      locationId: params.locationId,
    })
    return data
  }

  async getBasicProductInfo(params: { courseId: string; locationId: string }) {
    const { data } = await this.get(this.endpoint(`/${params.courseId}/resolve`, params.locationId), {
      productId: params.courseId,
      locationId: params.locationId,
    })
    return data
  }
}
